<template>
  <Modal v-model="isVisible" width="lg">
    <div slot="header">
      Revisão de Indicação em Lote
    </div>

    <div class="alert alert-info mb-4">
      <strong>Múltiplos motoristas detectados</strong><br>
      Você selecionou <strong>{{ totalTickets }}</strong> multas de <strong>{{ groupedDrivers.length }}</strong> motorista(s) diferente(s).
      <br>O sistema processará as indicações separadamente para cada motorista.
    </div>

    <p class="mb-3 review-description">
      Você selecionou multas com motoristas diferentes. Revise os grupos abaixo e selecione quais deseja processar:
    </p>

    <!-- Botões de seleção rápida -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <button
        class="btn btn-sm btn-outline"
        @click="selectAll"
        :disabled="allSelected"
      >
        Selecionar Todos
      </button>
      <button
        class="btn btn-sm btn-outline"
        @click="deselectAll"
        :disabled="noneSelected"
      >
        Desmarcar Todos
      </button>
    </div>

    <!-- Cards de motoristas agrupados -->
    <div class="driver-groups-container">
      <div
        v-for="group in groupedDrivers"
        :key="group.cpf"
        class="driver-group-card"
        :class="{ 'selected': group.selected }"
      >
        <div class="driver-group-header">
          <div class="d-flex align-items-center">
            <input
              type="checkbox"
              v-model="group.selected"
              class="cursor-pointer mr-3"
            />
            <div class="driver-info">
              <div class="driver-name">{{ group.driver.name }}</div>
              <div class="driver-details">
                <span>CPF: {{ formatCpf(group.cpf) }}</span>
                <span class="ml-3">Categoria: {{ group.driver.category || '-' }}</span>
              </div>
            </div>
          </div>
          <span class="badge badge-primary">{{ group.tickets.length }} multa{{ group.tickets.length > 1 ? 's' : '' }}</span>
        </div>

        <div class="driver-group-tickets">
          <div
            v-for="indication in group.tickets"
            :key="indication.id"
            class="ticket-item"
          >
            • AIT: <strong>{{ (indication.ticket && indication.ticket.ait) || indication.ait }}</strong> - Placa: <strong>{{ (indication.ticket && indication.ticket.plate) || indication.plate }}</strong>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-3 pt-3 border-top">
      <small class="text-muted">
        <strong>Total selecionado:</strong> {{ selectedCount }} motorista(s) com {{ selectedTicketsCount }} multa(s)
      </small>
    </div>

    <template slot="footer">
      <button @click="close" class="btn btn-outline mr-2">Cancelar</button>
      <button
        @click="confirmBatch"
        class="btn btn-primary"
        :disabled="selectedCount === 0"
      >
        Confirmar e Processar ({{ selectedCount }})
      </button>
    </template>
  </Modal>
</template>

<script>
import Modal from '@/components/shares/specialTag/Modal'

export default {
  components: { Modal },
  computed: {
    isVisible: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    allSelected() {
      return this.groupedDrivers.length > 0 && this.groupedDrivers.every(g => g.selected)
    },
    noneSelected() {
      return this.groupedDrivers.length > 0 && this.groupedDrivers.every(g => !g.selected)
    },
    selectedCount() {
      return this.groupedDrivers.filter(g => g.selected).length
    },
    selectedTicketsCount() {
      return this.groupedDrivers
        .filter(g => g.selected)
        .reduce((sum, g) => sum + g.tickets.length, 0)
    },
    totalTickets() {
      return this.selectedTickets.length
    }
  },
  data: () => ({
    groupedDrivers: []
  }),
  methods: {
    close() {
      this.isVisible = false
    },
    confirmBatch() {
      const selectedGroups = this.groupedDrivers.filter(g => g.selected)
      this.$emit('confirmBatch', selectedGroups)
    },
    formatCpf(cpf) {
      if (!cpf) return ''
      return cpf.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
    },
    groupTicketsByDriver() {
      const groups = {}

      this.selectedTickets.forEach(indication => {
        const cpf = indication.ticket?.cpf || indication.cpf
        if (cpf) {
          if (!groups[cpf]) {
            groups[cpf] = {
              cpf: cpf,
              driver: indication.driver || { name: 'Motorista não encontrado', category: '-' },
              tickets: [],
              selected: true
            }
          }
          groups[cpf].tickets.push(indication)
        }
      })

      this.groupedDrivers = Object.values(groups)
    },
    selectAll() {
      this.groupedDrivers.forEach(g => {
        g.selected = true
      })
    },
    deselectAll() {
      this.groupedDrivers.forEach(g => {
        g.selected = false
      })
    }
  },
  name: 'BatchReviewModal',
  props: {
    value: { required: true, type: Boolean },
    selectedTickets: { required: true, type: Array }
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.groupTicketsByDriver()
      }
    }
  }
}
</script>

<style scoped>
.alert-info {
  background-color: #d1ecf1;
  border-color: #bee5eb;
  color: #0c5460;
}

.alert-info strong {
  color: #0c5460;
}

[data-theme="dark"] .alert-info {
  background-color: #1a3a4a;
  border-color: #2a5a6a;
  color: #7ecfe6;
}

[data-theme="dark"] .alert-info strong {
  color: #7ecfe6;
}

.review-description {
  color: var(--terciary-text-color);
}

.driver-groups-container {
  max-height: 400px;
  overflow-y: auto;
}

.driver-group-card {
  background-color: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  margin-bottom: 12px;
  transition: all 0.2s ease;
}

.driver-group-card.selected {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 1px var(--primary-color);
}

.driver-group-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid var(--border-color);
}

.driver-info {
  flex: 1;
}

.driver-name {
  font-weight: 600;
  color: var(--primary-text-color);
  font-size: 15px;
}

.driver-details {
  color: var(--secondary-text-color);
  font-size: 13px;
  margin-top: 4px;
}

.driver-group-tickets {
  padding: 12px 16px;
  background-color: var(--light-gray);
  border-radius: 0 0 8px 8px;
}

.ticket-item {
  color: var(--terciary-text-color);
  font-size: 13px;
  padding: 4px 0;
}

.badge {
  font-size: 12px;
  padding: 6px 12px;
  border-radius: 12px;
}

.badge-primary {
  background-color: var(--primary-color);
  color: white;
}

/* Customização das checkboxes */
input[type="checkbox"] {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  width: 20px;
  height: 20px;
  border: 2px solid var(--border-color);
  border-radius: 4px;
  background-color: transparent;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

input[type="checkbox"]:checked {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
}

input[type="checkbox"]:checked::after {
  content: '';
  position: absolute;
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid white;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

input[type="checkbox"]:hover {
  border-color: var(--primary-color);
}

input[type="checkbox"]:focus {
  outline: none;
  box-shadow: 0 0 0 2px var(--primary-color-disabled);
}

.cursor-pointer {
  cursor: pointer;
}
</style>
